import React, {useState, useEffect, useContext} from 'react';
import {useLocation} from 'react-router-dom';
import {
    Table,
    Row,
    Col,
    Typography,
    Checkbox,
    notification,
    Button,
    Tag,
    Space,
    Popconfirm,
    Pagination,
    Divider,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../../restAPI';
import {AuthContext} from '../../../components/AuthContext';
import {orderStatuses} from '../../../services/orderStatuses';
import Loading from '../../../components/Loading';
import buildQueryParams from '../../../services/utilities/buildQueryParams';
import FilterPanel from './FilterPanel';
import AssignDriverModal from './AssignDriverModal';
import DetailsModal from './DetailsModal';

const {Title} = Typography;


// Custom Hook for Data Fetching
const useOrdersData = (token, filters, pagination) => {
    const [ordersData, setOrdersData] = useState({
        data: [],
        totalItems: 0,
        totalPages: 0,
        isLoading: false,
    });

    const fetchData = async () => {
        setOrdersData((prevState) => ({...prevState, isLoading: true}));

        const config = {headers: {Authorization: `Bearer ${token}`}};
        const params = {
            limit: pagination.limit,
            offset: pagination.offset,
            clientIdSearch: filters.clientIdSearch,
            clientNameSearch: filters.clientNameSearch,
            typeFilter: filters.typeFilter,
            statusFilter: filters.statusFilter,
            selectedDate: filters.selectedDate ? dayjs(filters.selectedDate).format('YYYY-MM-DD') : undefined,
            timeZoneOffset: new Date().getTimezoneOffset(),
            routeCodeSearch: filters.routeCodeSearch,
            driverIdSearch: filters.driverIdSearch,
            driverNameSearch: filters.driverNameSearch,
        };

        try {
            const response = await api.get(`/orders/all-orders/?${buildQueryParams(params)}`, config);
            setOrdersData({
                data: response.data.items,
                totalItems: response.data.count, // Ensure this matches what your backend returns
                isLoading: false,
            });
        } catch (error) {
            notification.error({message: 'Failed to fetch orders.'});
            console.error(error);
            setOrdersData((prevState) => ({...prevState, isLoading: false}));
        }
    };


    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, filters, pagination]);

    return {...ordersData, fetchData};
};

// Custom Hook for Fetching Drivers
const useDriversData = (token) => {
    const [driversData, setDriversData] = useState({
        drivers: [],
        loadingDrivers: false,
    });

    const fetchDrivers = async () => {
        setDriversData((prevState) => ({...prevState, loadingDrivers: true}));

        try {
            const response = await api.get('/driver/drivers-list/', {headers: {Authorization: `Bearer ${token}`}});
            setDriversData({drivers: response.data.drivers, loadingDrivers: false});
        } catch (err) {
            console.log("Error while fetching drivers", err);
            setDriversData((prevState) => ({...prevState, loadingDrivers: false}));
        }
    };

    useEffect(() => {
        fetchDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return driversData;
};

// Utility Functions
const formatDate = (date) => (dayjs(date).isValid() ? dayjs(date).tz('America/New_York').format('MM/DD/YYYY HH:mm A') : '-- : --');


const CustomPagination = ({pagination, setPagination, totalItems}) => {
    return (
        <div style={{display: 'flex', justifyContent: 'center'}}>
            <Pagination
                responsive={true}
                current={pagination.offset / pagination.limit + 1}
                total={totalItems}
                pageSize={pagination.limit}
                showSizeChanger={true} // Enable pageSize dropdown
                pageSizeOptions={['10', '20', '50', '100', '500']} // Options for items per page
                onChange={(page, pageSize) => {
                    setPagination((prevState) => ({
                        ...prevState,
                        limit: pageSize, // Update limit based on selected page size
                        offset: (page - 1) * pageSize, // Update offset based on selected page
                    }));
                }}
            />
        </div>
    );
};


// Main Component
const AllStops = () => {
    const location = useLocation();
    const {token} = useContext(AuthContext);
    const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false); // State for DetailsModal visibility
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null); // State for selected order details

    const getFiltersFromURL = () => {
        const searchParams = new URLSearchParams(location.search);

        return {
            clientIdSearch: searchParams.get('clientIdSearch') || '',
            clientNameSearch: searchParams.get('clientNameSearch') || '',
            statusFilter: searchParams.get('statusFilter') || '',
            typeFilter: searchParams.get('typeFilter') || '',
            selectedDate: searchParams.get('selectedDate') ? dayjs(searchParams.get('selectedDate')) : '',
            routeCodeSearch: searchParams.get('routeCodeSearch') || '',
            driverIdSearch: searchParams.get('driverIdSearch') || '',
            driverNameSearch: searchParams.get('driverNameSearch') || '',
        };
    };

    const [filters, setFilters] = useState(getFiltersFromURL());

    const [selectionState, setSelectionState] = useState({
        selectedOrders: [],
        checkAll: false,
        hoveredRow: null,
    });

    const [pagination, setPagination] = useState({
        limit: 50, // Default number of items per page
        offset: 0, // Start from the first item
    });

    // eslint-disable-next-line no-unused-vars
    const [modalState, setModalState] = useState({
        isModalVisible: false,
        newDriverId: null,
        selectedDriver: null,
    });

    const {data, totalItems, isLoading, fetchData} = useOrdersData(token, filters, pagination);


    // eslint-disable-next-line no-unused-vars
    const {drivers, loadingDrivers} = useDriversData(token);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // const handleModalVisibility = (isVisible) => {
    //     setModalVisible(isVisible);
    // };

    const handleSelectChange = (orderId) => {
        setSelectionState((prevState) => {
            const newSelectedOrders = prevState.selectedOrders.includes(orderId)
                ? prevState.selectedOrders.filter((id) => id !== orderId)
                : [...prevState.selectedOrders, orderId];

            return {
                ...prevState,
                selectedOrders: newSelectedOrders,
                checkAll: newSelectedOrders.length === data.length,
            };
        });
    };

    const handleCheckAllChange = (e) => {
        setSelectionState({
            selectedOrders: e.target.checked ? data.map((order) => order.id) : [],
            checkAll: e.target.checked,
        });
    };

    const handleFilterChange = (updatedFilters) => {
        setFilters(updatedFilters);
        fetchData();
    };

    // Update filters whenever the URL changes
    useEffect(() => {
        setFilters(getFiltersFromURL());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);


    const handleModalVisibility = (isVisible) => {
        console.log('Modal visibility:', isVisible); // Add this to debug
        setIsModalVisible(isVisible);
    };

    const handleReassignDriver = async (driverId) => {
        if (!driverId) {
            notification.error({message: 'Please select a driver.'});
            return;
        }

        try {
            const config = {headers: {Authorization: `Bearer ${token}`}};
            await api.put('/orders/reassign-driver/', {
                orderIds: selectionState.selectedOrders,
                driverId,
            }, config);

            notification.success({message: 'Driver reassigned successfully.'});
            handleModalVisibility(false);
            setSelectionState({selectedOrders: [], checkAll: false});
            fetchData();
        } catch (error) {
            notification.error({message: 'Failed to reassign driver.'});
            console.error(error);
        }
    };

    const handleCancelOrders = async () => {
        if (!selectionState.selectedOrders.length) {
            notification.error({message: 'Please select some orders to cancel.'});
            return;
        }

        try {
            const config = {headers: {Authorization: `Bearer ${token}`}};
            await api.put('/orders/cancel-orders/', {
                orderIds: selectionState.selectedOrders,
            }, config);

            notification.success({message: 'Orders canceled successfully.'});
            handleModalVisibility(false);
            setSelectionState({selectedOrders: [], checkAll: false});
            fetchData();
        } catch (error) {
            notification.error({message: 'Failed to cancel orders.'});
            console.error(error);
        }
    };

    // Columns for the table
    const columns = [
        {
            title: (filters.statusFilter === orderStatuses.CREATED || filters.statusFilter === orderStatuses.NOT_ASSIGNED) ? (
                <Checkbox checked={selectionState.checkAll} onChange={handleCheckAllChange}/>
            ) : null,
            dataIndex: 'select',
            key: 'select',
            align: 'center',
            render: (_, record) => (
                record && (record.status === orderStatuses.CREATED || record.status === orderStatuses.NOT_ASSIGNED) ? (
                    <Checkbox
                        checked={selectionState.selectedOrders.includes(record.id)}
                        onChange={() => handleSelectChange(record.id)}
                    />
                ) : null
            ),
        },

        {
            title: 'Created At',
            dataIndex: 'created',
            key: 'created',
            width: '100px',
            align: 'center',
            render: (text) => formatDate(text),
        },

        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            render: (text, record) => {
                const statusColors = {
                    [orderStatuses.CREATED]: '#888888',
                    [orderStatuses.NOT_ASSIGNED]: 'red',
                    [orderStatuses.CANCELED]: 'brown',
                    [orderStatuses.PICKED_UP]: 'green',
                    [orderStatuses.PICKED_UP_WITH_ISSUES]: 'black',
                    [orderStatuses.DROPPED_DOWN]: 'green',
                    [orderStatuses.PUSHED_TO_AIR_BILL]: 'green',
                    [orderStatuses.HANDED_OVER]: 'violet',
                    [orderStatuses.CONFIRMED]: 'blue',
                    [orderStatuses.RECEIVED_WITH_ISSUE]: 'black',
                };

                const typeColors = {
                    SCHEDULED: '#888888',
                    ONCALLSTAT: 'red',
                    ONCALLROUTINE: 'red',
                };
                const badgeText = record.type === 'SCHEDULED' ? 'Regular stop' : (record.type === 'ONCALLSTAT' || record.type === 'ONCALLROUTINE') ? 'OnCall stop' : record.type.toUpperCase();


                const getOrderSource = (entryUser) => {
                    if (entryUser.startsWith('CS:')) return 'client service';
                    if (entryUser.startsWith('LOG:')) return 'old logistic';
                    if (entryUser.startsWith('W:')) return 'welcome';
                    if (entryUser === 'user') return 'auto-generated';
                    return entryUser;
                };

                const orderSource = getOrderSource(record.entryUser);


                return (
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                        <div
                            style={{
                                backgroundColor: statusColors[text] || 'cyan',
                                width: '100%',
                                textAlign: 'center',
                                color: 'white',
                                borderRadius: '7px 7px 0 0 ',
                            }}>
                            {text.toUpperCase()}
                        </div>
                        <div style={{
                            backgroundColor: typeColors[record.type] || 'cyan',
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '11px',
                            borderRadius: '0 0  7px 7px ',
                            color: 'white',
                            padding: '2px',
                        }}>
                            {badgeText}
                            <br/>
                            {orderSource}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Confirmation ID',
            dataIndex: 'confirmationId',
            key: 'confirmationId',
            align: 'center',
            render: (routeCode) => routeCode ? <Tag bordered={false}>{routeCode}</Tag> : 'N/A',
        },
        {
            title: 'Detailed',
            key: 'detailed',
            align: 'center',
            render: (_, record) => (
                <Button type="primary" size="small" onClick={() => showDetailsModal(record)}>
                    Detailed
                </Button>
            ),
        },
        {
            title: 'Route Code',
            dataIndex: ['routeDetails', 'code'],
            key: 'route_code',
            align: 'center',
            render: (routeCode) => routeCode ? <Tag bordered={false}>{routeCode}</Tag> : 'N/A',
        },
        {
            title: 'Client ID',
            dataIndex: ['clientDetails', 'id'],
            key: 'client_id',
            align: 'center',
        },
        {
            title: 'Client Name',
            dataIndex: 'clientDetails',
            key: 'client_name',
            align: 'left',
            render: (clientDetails) => clientDetails ? (
                <>
                    <Typography.Paragraph style={{margin: 0}}>
                        {clientDetails.name}
                    </Typography.Paragraph>
                    <Typography.Text type={'secondary'}>
                        {clientDetails.street ? `${clientDetails.street}, ` : ''}
                        {clientDetails.city ? `${clientDetails.city}, ` : ''}
                        {clientDetails.state ? `${clientDetails.state}, ` : ''}
                        {clientDetails.zip ? `${clientDetails.zip}` : ''}
                    </Typography.Text>
                </>
            ) : 'N/A',
        },
        {
            title: 'HANDED OVER TO',
            dataIndex: 'handedOverTo',
            key: 'handedOverTo',
            align: 'center',
            render: handedOverTo => <Typography.Text>{handedOverTo}</Typography.Text>
        },
        {
            title: 'Driver',
            dataIndex: 'driverDetails',
            key: 'driver',
            align: 'left',
            render: (driverDetails) => driverDetails ? (
                <>
                    <Typography.Paragraph style={{margin: 0}}>
                        {driverDetails.first_name ? `${driverDetails.first_name} ${driverDetails.last_name}` : 'N/A'}
                    </Typography.Paragraph>
                    <Typography.Text type={'secondary'}>
                        {driverDetails.phone || 'N/A'}
                    </Typography.Text>
                </>
            ) : 'N/A',
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            align: 'left',
            render: (text) => text || 'N/A',
        },

        {
            title: 'PickedUp At',
            dataIndex: 'picked_up_date',
            key: 'picked_up_date',
            width: '100px',
            align: 'center',
            render: (text) => formatDate(text),
        },
        {
            title: 'Handed At',
            dataIndex: 'handedOverAt',
            key: 'handedOverAt',
            width: '100px',
            align: 'center',
            render: (text) => formatDate(text),
        },
        {
            title: 'Received At',
            dataIndex: 'receivedAt',
            key: 'receivedAt',
            width: '100px',
            align: 'center',
            render: (text) => formatDate(text),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: '100px',
            align: 'center',
            render: (text) => text || 'TBD',
        },
    ];


    // Function to handle showing the details modal
    const showDetailsModal = (order) => {
        setSelectedOrderDetails(order);
        setIsDetailsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsDetailsModalVisible(false);
        setSelectedOrderDetails(null);
    };


    return (
        <Row justify="center" align="middle">
            <Col xl={23} lg={23} md={23} sm={23} xs={23}>
                <Row justify="center">
                    <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                        <Title level={2} className="mainLabel">All Stops</Title>
                    </Col>

                    <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                        <FilterPanel
                            token={token}
                            filters={filters}
                            setFilters={setFilters}
                            onFilterChange={handleFilterChange}
                        />


                        <Col xl={24} lg={24} md={24} sm={24} xs={24} style={{marginBottom: 25}}>
                            <Space>
                                <Button
                                    size={'small'}
                                    type="primary"
                                    onClick={() => {
                                        console.log('Button clicked'); // Add this to debug
                                        handleModalVisibility(true);
                                    }}
                                    disabled={selectionState.selectedOrders.length === 0}
                                >
                                    Reassign Driver
                                </Button>

                                <Popconfirm
                                    title="Are you sure you want to cancel the selected orders?"
                                    onConfirm={handleCancelOrders}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        danger={true}
                                        size={'small'}
                                        type="primary"
                                        disabled={selectionState.selectedOrders.length === 0}
                                    >
                                        Cancel order(s)
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </Col>

                        {/* Pagination at the top */}
                        <Col xl={24} lg={24} md={24} sm={24} xs={24} style={{marginBottom: 10}}>
                            <CustomPagination
                                pagination={pagination}
                                setPagination={setPagination}
                                totalItems={totalItems}
                            />
                        </Col>
                        <Col xl={24} lg={24} md={24} sm={24} xs={24} style={{margin: 0}}>
                            <Space>
                                <Typography.Paragraph type={'secondary'}>
                                    Checked orders: {selectionState.selectedOrders.length}
                                </Typography.Paragraph>

                                <Typography.Paragraph type={'secondary'}>
                                    Total Items: {totalItems || 0}
                                </Typography.Paragraph>
                            </Space>
                        </Col>


                        <Loading loading={isLoading}>
                            <Table
                                columns={columns}
                                dataSource={data}
                                rowKey="id"
                                bordered
                                size="small"
                                pagination={false}
                            />

                        </Loading>

                        <Row gutter={[24, 24]}>

                            <Divider style={{marginTop: 0}}/>

                            {/* Pagination at the bottom */}
                            <Col xl={24} lg={24} md={24} sm={24} xs={24} style={{marginBottom: 10}}>
                                <CustomPagination
                                    pagination={pagination}
                                    setPagination={setPagination}
                                    totalItems={totalItems}
                                />
                            </Col>

                            <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                                <Space>
                                    <Typography.Paragraph type={'secondary'}>
                                        Checked orders: {selectionState.selectedOrders.length}
                                    </Typography.Paragraph>

                                    <Typography.Paragraph type={'secondary'}>
                                        Total Items: {totalItems || 0}
                                    </Typography.Paragraph>
                                </Space>
                            </Col>
                        </Row>

                    </Col>
                </Row>
            </Col>

            {/* <Modal
                title="Assign/Reassign Driver"
                open={modalState.isModalVisible}
                onOk={handleReassignDriver}
                onCancel={() => handleModalVisibility(false)}
            >
                <Select
                    showSearch
                    placeholder="Select Driver"
                    onChange={handleDriverChange}
                    value={modalState.selectedDriver || undefined} // Ensure placeholder shows when no value is selected
                    loading={loadingDrivers}
                    allowClear
                    filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    onClear={() => setModalState((prevState) => ({
                        ...prevState,
                        selectedDriver: null,
                        newDriverId: null
                    }))}
                    style={{width: '100%'}}
                >
                    {filteredDrivers.map(driver => (
                        <Option key={driver.id} value={driver.id}>
                            {`${driver.first_name} ${driver.last_name}`}
                        </Option>
                    ))}
                </Select>
            </Modal>*/}

            {/* DetailsModal Component */}
            <DetailsModal
                token={token}
                isModalVisible={isDetailsModalVisible}
                selectedRowData={selectedOrderDetails}
                handleModalClose={handleModalClose}
            />


            <AssignDriverModal
                isModalVisible={isModalVisible}
                handleModalVisibility={handleModalVisibility}
                onAssign={handleReassignDriver}
                token={token}
                orderIds={selectionState.selectedOrders}
            />
        </Row>
    );
};

export default AllStops;
